// src/belediye/redux/slices/belediyeAuthSlice.js

import { createSlice } from "@reduxjs/toolkit";

const LS_KEYS = {
  //   Yeni standart
  accessToken: "accessToken",
  refreshToken: "refreshToken",
  user: "user",

  //   Multi-unit
  activeUnitSlug: "activeUnitSlug",

  //   Eski/legacy (backward compat)
  legacyToken: "token",
};

/**
 * Bazı eski kodlar token'ı JSON.stringify ile yazdığı için
 * localStorage'da "\"abc\"" gibi tutulmuş olabilir.
 * Bu helper hem raw string hem de JSON-stringify edilmiş string’i düzgün okur.
 */
const readMaybeJSONString = (key) => {
  const v = localStorage.getItem(key);
  if (!v) return null;

  // İlk deneme: JSON parse (ör: "\"abc\"" -> "abc")
  try {
    const parsed = JSON.parse(v);
    if (typeof parsed === "string") return parsed;
    // Eğer obje vs ise burada token olmayacak; fallback
  } catch {
    // ignore
  }

  // Fallback: raw value (ör: "abc")
  return v;
};

const writeString = (key, value) => {
  if (value === null || value === undefined || value === "") {
    localStorage.removeItem(key);
  } else {
    localStorage.setItem(key, value);
  }
};

const readJSON = (key) => {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : null;
  } catch {
    return null;
  }
};

const writeJSON = (key, value) => {
  if (value === null || value === undefined) {
    localStorage.removeItem(key);
  } else {
    localStorage.setItem(key, JSON.stringify(value));
  }
};

//   İlk yükleme: accessToken yoksa legacy token’dan dene (bozmadan geçiş)
const initialAccessToken =
  readMaybeJSONString(LS_KEYS.accessToken) || readMaybeJSONString(LS_KEYS.legacyToken);

const initialRefreshToken = readMaybeJSONString(LS_KEYS.refreshToken);

const initialState = {
  accessToken: initialAccessToken,
  refreshToken: initialRefreshToken,
  user: readJSON(LS_KEYS.user),

  // Multi-unit
  activeUnitSlug: readMaybeJSONString(LS_KEYS.activeUnitSlug),

  isAuthenticated: Boolean(initialAccessToken),
  error: null,
};

const belediyeAuthSlice = createSlice({
  name: "belediyeAuth",
  initialState,
  reducers: {
    /**
     *   Tokenları tek seferde yönet
     * payload: { access, refresh }
     */
    setTokens(state, action) {
      const { access, refresh } = action.payload || {};

      state.accessToken = access || null;
      state.refreshToken = refresh || null;
      state.isAuthenticated = Boolean(access);
      state.error = null;

      writeString(LS_KEYS.accessToken, access || null);
      writeString(LS_KEYS.refreshToken, refresh || null);

      // Legacy key’i temizleyelim (geçiş tamam)
      localStorage.removeItem(LS_KEYS.legacyToken);
    },

    /**
     *   Kullanıcıyı kaydet
     * Not: Auth olma durumunu token belirler.
     */
    setUser(state, action) {
      state.user = action.payload || null;
      state.error = null;
      writeJSON(LS_KEYS.user, state.user);
    },

    /**
     *   Multi-unit: seçilen birimi hatırla
     */
    setActiveUnitSlug(state, action) {
      const slug = action.payload || null;
      state.activeUnitSlug = slug;
      state.error = null;
      writeString(LS_KEYS.activeUnitSlug, slug);
    },

    /**
     *   Uygulama açılışında storage -> state hydrate
     * (refresh sonrasında veya hard reload'da güvenli)
     */
    hydrateFromStorage(state) {
      const access =
        readMaybeJSONString(LS_KEYS.accessToken) || readMaybeJSONString(LS_KEYS.legacyToken);
      const refresh = readMaybeJSONString(LS_KEYS.refreshToken);
      const user = readJSON(LS_KEYS.user);
      const activeUnitSlug = readMaybeJSONString(LS_KEYS.activeUnitSlug);

      state.accessToken = access;
      state.refreshToken = refresh;
      state.user = user;
      state.activeUnitSlug = activeUnitSlug;
      state.isAuthenticated = Boolean(access);
    },

    /**
     *   Komple logout / session clear
     */
    logout(state) {
      state.accessToken = null;
      state.refreshToken = null;
      state.user = null;
      state.activeUnitSlug = null;
      state.isAuthenticated = false;
      state.error = null;

      localStorage.removeItem(LS_KEYS.accessToken);
      localStorage.removeItem(LS_KEYS.refreshToken);
      localStorage.removeItem(LS_KEYS.user);
      localStorage.removeItem(LS_KEYS.activeUnitSlug);
      localStorage.removeItem(LS_KEYS.legacyToken);
    },

    setError(state, action) {
      state.error = action.payload || null;
    },

    clearError(state) {
      state.error = null;
    },
  },
});

export const {
  setTokens,
  setUser,
  setActiveUnitSlug,
  hydrateFromStorage,
  logout,
  setError,
  clearError,
} = belediyeAuthSlice.actions;

export default belediyeAuthSlice.reducer;
